import {AppleIcon} from '@github-ui/custom-icons/AppleIcon'
import {LinuxIcon} from '@github-ui/custom-icons/LinuxIcon'
import {WindowsIcon} from '@github-ui/custom-icons/WindowsIcon'
import {getOS, OS, type OS as OperatingSystem} from '@github-ui/get-os'
import {sendEvent} from '@github-ui/hydro-analytics'
import {reportIpmAction, type IpmMetricTags} from '@github-ui/ipm-telemetry'
import {TriangleDownIcon} from '@primer/octicons-react'
import type {Icon} from '@primer/octicons-react'
import {ActionList, ActionMenu, ButtonGroup, IconButton, Label, LinkButton} from '@primer/react'
import type {CSSProperties} from 'react'
import {useMemo, useSyncExternalStore} from 'react'

import backgroundDark from './assets/background-dark.png'
import backgroundLight from './assets/background-light.png'
import bannerDark from './assets/banner-dark.png'
import bannerLight from './assets/banner-light.png'
import styles from './CopilotAppBanner.module.css'
import {IpmBannerBase} from '../ipm-banner-base/IpmBannerBase'

export type CopilotAppDownloadPlatform = 'mac' | 'windows' | 'linux'

export interface CopilotAppDownloadItem {
  id: string
  label: string
  platform: CopilotAppDownloadPlatform
  href: string
  analyticsAction: string
}

export interface CopilotAppBannerAnalytics {
  category: string
  label: string
  location: string
}

export interface CopilotAppBannerPayload {
  label: string
  title: string
  dismissPath: string
  analytics: CopilotAppBannerAnalytics
  download: {
    label: string
    href: string
    analyticsAction: string
    menuAnalyticsAction: string
    items: CopilotAppDownloadItem[]
  }
  testSelector: string
}

export interface CopilotAppBannerProps {
  banner: CopilotAppBannerPayload
  rounded?: boolean
  downloadPlatform?: CopilotAppDownloadPlatform
}

const ICON_BY_PLATFORM: Record<CopilotAppDownloadPlatform, Icon> = {
  mac: AppleIcon,
  windows: WindowsIcon,
  linux: LinuxIcon,
}

const DOWNLOAD_LABEL_BY_PLATFORM: Record<CopilotAppDownloadPlatform, string> = {
  mac: 'Download for macOS',
  windows: 'Download for Windows',
  linux: 'Download for Linux',
}

const DEFAULT_DOWNLOAD_PLATFORM: CopilotAppDownloadPlatform = 'mac'
const COPILOT_APP_TITLE = 'The GitHub Copilot app'
const COPILOT_APP_DESCRIPTION = 'An agent-driven desktop experience built natively on GitHub.'
const ARTWORK_STYLE = {
  '--copilot-app-banner-light': `url(${bannerLight})`,
  '--copilot-app-banner-dark': `url(${bannerDark})`,
  '--copilot-app-banner-light-compact': `url(${backgroundLight})`,
  '--copilot-app-banner-dark-compact': `url(${backgroundDark})`,
} as CSSProperties

function subscribeToDownloadPlatform() {
  return () => {}
}

export function resolveDownloadPlatform(os: OperatingSystem): CopilotAppDownloadPlatform {
  switch (os) {
    case OS.Windows:
      return 'windows'
    case OS.Linux:
      return 'linux'
    case OS.macOS:
    default:
      return DEFAULT_DOWNLOAD_PLATFORM
  }
}

function getBrowserDownloadPlatform(): CopilotAppDownloadPlatform {
  return resolveDownloadPlatform(getOS().os)
}

function getServerDownloadPlatform(): CopilotAppDownloadPlatform {
  return DEFAULT_DOWNLOAD_PLATFORM
}

function useDetectedDownloadPlatform() {
  return useSyncExternalStore(subscribeToDownloadPlatform, getBrowserDownloadPlatform, getServerDownloadPlatform)
}

function analyticsLabel(analytics: CopilotAppBannerAnalytics) {
  return `${analytics.label}location:${analytics.location};`
}

function analyticsEvent(analytics: CopilotAppBannerAnalytics, action: string) {
  return JSON.stringify({
    category: analytics.category,
    action,
    label: analyticsLabel(analytics),
  })
}

export function CopilotAppBanner({banner, rounded = true, downloadPlatform}: CopilotAppBannerProps) {
  const {analytics, download} = banner
  const detectedDownloadPlatform = useDetectedDownloadPlatform()
  const resolvedDownloadPlatform = downloadPlatform ?? detectedDownloadPlatform
  const primaryDownload = download.items.find(item => item.platform === resolvedDownloadPlatform)
  const primaryDownloadLabel = primaryDownload ? DOWNLOAD_LABEL_BY_PLATFORM[resolvedDownloadPlatform] : download.label
  const primaryDownloadHref = primaryDownload?.href ?? download.href
  const primaryDownloadAnalyticsAction = primaryDownload?.analyticsAction ?? download.analyticsAction

  const telemetryTags = useMemo<IpmMetricTags>(
    () => ({
      package: 'in-product-targeting',
      component: 'CopilotAppBanner',
      surface: 'banner',
      display_location: analytics.location,
      category: analytics.category,
      nudge_id: analytics.category,
      variant: 'download',
    }),
    [analytics.category, analytics.location],
  )

  return (
    <IpmBannerBase
      dismissPath={banner.dismissPath}
      visibleEvent={analyticsEvent(analytics, 'visible')}
      dismissEvent={analyticsEvent(analytics, 'dismiss')}
      testId={banner.testSelector}
      title={`${COPILOT_APP_TITLE}, ${banner.label}`}
      location={analytics.location}
      rounded={rounded}
      hideLeadingVisual
      className={styles.Surface}
      style={ARTWORK_STYLE}
      telemetryTags={telemetryTags}
      description={
        <span className={styles.Content}>
          <span className={styles.Heading} aria-hidden="true">
            <span className={styles.Title}>{COPILOT_APP_TITLE}</span>
            <Label variant="success" className={styles.Label}>
              {banner.label}
            </Label>
          </span>
          <span className={styles.Description}>{COPILOT_APP_DESCRIPTION}</span>
        </span>
      }
      primaryAction={
        <ButtonGroup className={styles.ActionGroup}>
          <LinkButton
            href={primaryDownloadHref}
            target="_blank"
            rel="noopener noreferrer"
            data-analytics-event={analyticsEvent(analytics, primaryDownloadAnalyticsAction)}
            onClick={() => reportIpmAction('cta_click', telemetryTags)}
          >
            {primaryDownloadLabel}
          </LinkButton>
          <ActionMenu
            onOpenChange={open => {
              // The trigger also closes an open menu, so a declarative data-analytics-event here
              // would record an "opened" action on the way back out too.
              if (!open) return
              reportIpmAction('click', {...telemetryTags, variant: 'download_menu'})
              sendEvent('analytics.click', {
                category: analytics.category,
                action: download.menuAnalyticsAction,
                label: analyticsLabel(analytics),
              })
            }}
          >
            <ActionMenu.Anchor>
              <IconButton icon={TriangleDownIcon} aria-label="Choose a GitHub Copilot app download" />
            </ActionMenu.Anchor>
            <ActionMenu.Overlay align="end">
              <ActionList>
                {download.items.map(item => {
                  const ItemIcon = ICON_BY_PLATFORM[item.platform]
                  return (
                    <ActionList.LinkItem
                      key={item.id}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={item.id}
                      data-analytics-event={analyticsEvent(analytics, item.analyticsAction)}
                      onClick={() => reportIpmAction('cta_click', {...telemetryTags, variant: item.platform})}
                    >
                      <ActionList.LeadingVisual>
                        <ItemIcon />
                      </ActionList.LeadingVisual>
                      {item.label}
                    </ActionList.LinkItem>
                  )
                })}
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        </ButtonGroup>
      }
    />
  )
}
